import CustomPaginationComponent from "@/components/common/custom-pagination-component";
import { SearchBar } from "@/components/common/search-bar";
import { ContentLayout } from "@/components/common/content-layout";
import { XIcon } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useState } from "react";
import useEmails, { useRetryEmail } from "@/features/emails/hooks/use-emails";
import { EmailCard } from "@/features/emails/ui/email-card";
import { EmailFilters } from "@/features/emails/ui/email-filters";
import { EmailRecord } from "@/features/emails/services/get-emails";
import { EmailBuilderTab } from "@/features/emails/ui/email-builder-tab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

type TabKey = "sent" | "builder";

function isTabKey(value: string | null): value is TabKey {
  return value === "sent" || value === "builder";
}

function SentEmailsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") || "0", 10);
  const search = searchParams.get("search") || "";
  const status = searchParams.get("status");
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");

  const [retryingEmailId, setRetryingEmailId] = useState<string | null>(null);

  const filterParts: string[] = [];
  if (status && status !== "ALL") {
    filterParts.push(`status::${status}`);
  }
  if (startDate) {
    filterParts.push(`start_date::${startDate}`);
  }
  if (endDate) {
    filterParts.push(`end_date::${endDate}`);
  }

  const filter = filterParts.join(",");

  const { data, error, isFetching, isLoading } = useEmails({
    page,
    search,
    filter,
  });

  const { mutate: retryEmail, isPending: isRetryPending } = useRetryEmail();

  const emails = data?.emails || [];
  const currentPage = data?.page || 0;
  const lastPage = data ? data.lastPage + 1 : 0;

  const handleRetry = (emailId: string) => {
    setRetryingEmailId(emailId);
    retryEmail(emailId, {
      onSettled: () => setRetryingEmailId(null),
    });
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < lastPage) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", newPage.toString());
      setSearchParams(newParams);
    }
  };

  return (
    <div className="w-full">
      <div className="bg-white p-4 rounded-lg mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <EmailFilters />

          <div className="flex-1 min-w-[200px]">
            <SearchBar
              placeholder="Search Emails"
              urlParamStrategy="prefix"
              clearButton={{
                enabled: true,
                position: "inside",
                ariaLabel: "Clear search",
                icon: <XIcon className="w-4 h-4" />,
              }}
              fieldSelector={{
                enabled: false,
                fields: [],
              }}
              className="w-full"
            />
          </div>
        </div>
      </div>

      <ContentLayout
        header={null}
        data={emails}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        errorMessage="Failed to load emails"
        emptyState={{
          message: "No emails found",
        }}
      >
        <div className="space-y-4">
          {emails.map((email: EmailRecord) => (
            <EmailCard
              key={email.id}
              email={email}
              onRetry={handleRetry}
              isRetrying={
                isRetryPending && retryingEmailId === email.id
              }
            />
          ))}
        </div>

        {emails.length > 0 && (
          <CustomPaginationComponent
            currentPage={currentPage}
            lastPage={lastPage}
            onPageChange={handlePageChange}
          />
        )}
      </ContentLayout>
    </div>
  );
}

export default function Emails() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab: TabKey = isTabKey(tabParam) ? tabParam : "sent";

  const handleTabChange = (next: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (next === "sent") {
      newParams.delete("tab");
    } else {
      newParams.set("tab", next);
    }
    setSearchParams(newParams);
  };

  return (
    <div className="w-full">
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="mb-4">
          <TabsTrigger value="sent">Sent</TabsTrigger>
          <TabsTrigger value="builder">Builder</TabsTrigger>
        </TabsList>

        <TabsContent value="sent">
          <SentEmailsTab />
        </TabsContent>

        <TabsContent value="builder">
          <EmailBuilderTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
